/**
 * The roguelike layer behind `GameApi` (04-roguelike-layer). Pure: storage and fresh seeds are
 * injected. Every mutating call is all-or-nothing - on an Error the profile rolls back to the
 * last save - and ends with the profile written as versioned JSON. Everything returned is a
 * copy; callers can never reach the live profile.
 */

import { reportOperation } from './aftermath';
import { takePerk } from './aftermath/merit';
import type { Deployment, GameApi, Storage } from './api';
import { induct, retire } from './career';
import { deployClassic, recruitClassic, reportClassic, retireClassic } from './classic';
import { blankRecord, cleanName } from './company';
import { dailyStatus, deployDaily, reportDaily, unchangedAftermath } from './daily';
import { deployOperation, practiceDeployment, startOperation, withdraw } from './deploy';
import { describeId, describeNode } from './describe';
import { buyIntel, offersFor } from './intel';
import { priceList } from './loadout';
import { openNodes } from './map';
import { deriveRecords } from './records';
import { loadProfile, saveProfile } from './save';
import { findRanger, type FullSummary, type ProfileState, snapshot } from './state';
import { hqService, visitNode } from './support';
import { trainPerk } from './tags';

export { SAVE_KEY } from './save';
export { dailyMission, validDate } from './daily';
export { generateOperation, validateOperation } from './opgen';
export { intelOffers } from './intel';
export { cacheItem, depotCandidates } from './support';
export { rewardRange } from './aftermath/tally';
export { nodeProgress, threatCap, threatFor } from './threat';
export type { ArchetypeStats, OperationState, PendingDeployment, ProfileState, SummaryExtras } from './state';

/** `seedSource` supplies fresh unsigned 32-bit seeds (the app owns the entropy; this layer never rolls its own). */
export function createGameApi(storage: Storage, seedSource: () => number): GameApi {
  let profile = loadProfile(storage);
  let saved = JSON.stringify(profile);

  function mutate<T>(change: (p: ProfileState) => T): T {
    try {
      const out = change(profile);
      saved = saveProfile(storage, profile);
      return out === undefined ? out : snapshot(out);
    } catch (err) {
      profile = JSON.parse(saved) as ProfileState;
      throw err;
    }
  }

  return {
    profile: () => snapshot(profile),

    startOperation: (difficulty, seed, cadet, veteranIds) =>
      mutate((p) => startOperation(p, difficulty, Number.isFinite(seed) ? seed : seedSource(), cadet, veteranIds)),
    withdrawOperation: () => mutate(withdraw),
    openNodes: () => (profile.operation ? snapshot(openNodes(profile.operation)) : []),
    visitSupportNode: (nodeId, choice) =>
      mutate((p) => (nodeId.startsWith('hq:') ? hqService(p, nodeId, choice) : visitNode(p, nodeId, choice))),

    prices: (rangerId) => {
      const classic = profile.classicRoster.some((r) => r.id === rangerId);
      return priceList(findRanger(profile, rangerId), classic);
    },
    deploy: (nodeId, rangerId, loadout) => mutate((p) => deployOperation(p, nodeId, rangerId, loadout)),
    deployClassic: (params, rangerId, loadout) => snapshot(deployClassic(profile, params, rangerId, loadout)),
    practice: (params) => practiceDeployment(params),

    report: (deployment: Deployment, summary: FullSummary) => {
      if (deployment.mode === 'practice') {
        return unchangedAftermath(profile, blankRecord('practice', 0, 'Practice', 'Ranger', 'CADET'));
      }
      const debrief = deployment.mode === 'daily' ? reportDaily
        : deployment.mode === 'classic' ? reportClassic : reportOperation;
      return mutate((p) => debrief(p, deployment, summary));
    },
    choosePerk: (rangerId, perk) => mutate((p) => {
      const ranger = findRanger(p, rangerId);
      if (!ranger) throw new Error('No such ranger.');
      takePerk(ranger, perk);
      return ranger;
    }),

    renameRanger: (rangerId, firstName, surname, callsign) => mutate((p) => {
      const ranger = findRanger(p, rangerId);
      if (!ranger) throw new Error('No such ranger.');
      // The original let you name your ranger; once he has flown, the name is his (04 §4.1).
      if (ranger.record.missions > 0 || p.operation?.deployed?.rangerId === rangerId) {
        throw new Error('A ranger can only be renamed before the first deployment.');
      }
      ranger.firstName = cleanName(firstName, 'first name');
      ranger.surname = cleanName(surname, 'surname');
      ranger.callsign = cleanName(callsign, 'callsign').toUpperCase();
      return ranger;
    }),
    inductVeteran: (rangerId) => mutate((p) => induct(p, rangerId)),
    retireVeteran: (rangerId) => mutate((p) => retire(p, rangerId)),
    recruitClassic: (firstName, surname, callsign) =>
      mutate((p) => recruitClassic(p, seedSource(), firstName, surname, callsign)),
    retireClassic: (rangerId) => mutate((p) => retireClassic(p, rangerId)),

    intelOffers: (nodeId) => {
      const op = profile.operation;
      const node = op?.nodes.find((n) => n.id === nodeId);
      return op && node ? offersFor(op, node) : [];
    },
    buyIntel: (nodeId, intel) => mutate((p) => void buyIntel(p, nodeId, intel)),
    stock: () => snapshot(profile.operation?.status === 'active' ? profile.operation.stock : {}),
    trainPerk: (rangerId, perk) => mutate((p) => trainPerk(p, rangerId, perk)),
    daily: (dateUtc) => snapshot(dailyStatus(profile, dateUtc)),
    deployDaily: (dateUtc) => mutate((p) => deployDaily(p, dateUtc)),
    records: () => deriveRecords(profile),

    describe: (id) => {
      const node = profile.operation?.nodes.find((n) => n.id === id);
      return node && profile.operation ? describeNode(profile.operation, node) : describeId(id);
    },
  };
}
